(() => {
  if (window.__zingCartUpsellInitialized) return;
  window.__zingCartUpsellInitialized = true;

  const FREE_SHIPPING_CENTS = 8000;
  const MAX_POST_FREE_SHIPPING_UPSELL_CENTS = 1500;
  const html = document.documentElement;
  const cartUrl = () => (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
  const cartAddUrl = () => (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';

  const markDrawerOpen = () => html.classList.add('zing-cart-drawer-open');
  const clearDrawerGuard = () => html.classList.remove('zing-cart-drawer-open');

  async function getCart() {
    const response = await fetch(`${cartUrl()}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  function getCandidates(cart) {
    const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
    const seen = new Set();

    return [...document.querySelectorAll('template[data-zing-drawer-upsell-template]')]
      .map((template) => ({
        template,
        productId: String(template.dataset.zingProductId || ''),
        price: Number(template.dataset.zingPriceCents || 0)
      }))
      .filter((candidate) => {
        if (!candidate.productId || candidate.price <= 0) return false;
        if (inCart.has(candidate.productId) || seen.has(candidate.productId)) return false;
        seen.add(candidate.productId);
        return true;
      })
      .sort((a, b) => a.price - b.price);
  }

  function chooseCandidate(cart) {
    const candidates = getCandidates(cart);
    if (!candidates.length || Number(cart.item_count || 0) < 1) return null;

    const total = Number(cart.total_price || 0);
    if (total < FREE_SHIPPING_CENTS) {
      const gap = FREE_SHIPPING_CENTS - total;
      const crossing = candidates.find((candidate) => candidate.price >= gap);
      return { candidate: crossing || candidates[0], mode: crossing ? 'cross' : 'easy' };
    }

    const smallExtra = candidates.find(
      (candidate) => candidate.price <= MAX_POST_FREE_SHIPPING_UPSELL_CENTS
    );
    return smallExtra ? { candidate: smallExtra, mode: 'extra' } : null;
  }

  function decorate(node, mode) {
    if (!node) return;
    const badge = node.querySelector('[data-zing-upsell-badge]');
    if (!badge) return;

    const key = mode === 'cross'
      ? 'zingLabelCross'
      : mode === 'extra'
        ? 'zingLabelExtra'
        : 'zingLabelEasy';
    badge.textContent = badge.dataset[key] || badge.textContent;
  }

  async function hydrateDrawerUpsell(cart) {
    const slots = document.querySelectorAll('[data-zing-drawer-upsell-slot]');
    if (!slots.length) return false;

    try {
      const currentCart = cart || await getCart();
      if (window.theme) window.theme.cartItemCount = currentCart.item_count;
      const choice = chooseCandidate(currentCart);

      slots.forEach((slot) => {
        slot.replaceChildren();
        if (!choice) return;
        const clone = choice.candidate.template.content.cloneNode(true);
        decorate(clone.firstElementChild, choice.mode);
        slot.appendChild(clone);
      });
      return Boolean(choice);
    } catch (error) {
      console.warn('ZingCandy cart upsell could not refresh.', error);
      return false;
    }
  }

  function scheduleHydration(cart) {
    hydrateDrawerUpsell(cart);
    window.setTimeout(() => hydrateDrawerUpsell(), 120);
    window.setTimeout(() => hydrateDrawerUpsell(), 420);
  }

  async function addFromForm(form) {
    const button = form.querySelector('[data-zing-add-button]');
    const errorEl = form.closest('[data-zing-upsell-card]')?.querySelector('[data-zing-add-error]');
    const original = button ? button.textContent : '';
    const addingLabel = button?.dataset.addingLabel || 'Adding…';
    const addedLabel = button?.dataset.addedLabel || 'Added';

    if (button) {
      button.disabled = true;
      button.textContent = addingLabel;
    }
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    try {
      const response = await fetch(`${cartAddUrl()}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form),
        credentials: 'same-origin'
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item.');

      const cart = await getCart();
      if (window.theme) window.theme.cartItemCount = cart.item_count;
      document.dispatchEvent(new CustomEvent('theme:update:cart'));
      document.dispatchEvent(new CustomEvent('theme:product:add', {
        detail: {
          cartItemCount: cart.item_count,
          variantId: form.querySelector('[name="id"]')?.value || null,
          itemsRootEl: document.querySelector('[data-cart-items]'),
          lineItemEl: null,
          key: null
        }
      }));

      if (button) button.textContent = addedLabel;
      window.setTimeout(() => scheduleHydration(cart), 250);
    } catch (error) {
      if (button) {
        button.disabled = false;
        button.textContent = original;
      }
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-zing-cart-add]');
    if (!form) return;
    event.preventDefault();
    addFromForm(form);
  });

  document.body.addEventListener('cart-will-open', markDrawerOpen);
  document.body.addEventListener('cart-is-open', () => {
    markDrawerOpen();
    scheduleHydration();
  });
  document.body.addEventListener('cart-will-close', markDrawerOpen);
  document.body.addEventListener('cart-is-closed', clearDrawerGuard);

  document.addEventListener('theme:cart:change', (event) => {
    if (event.detail && typeof event.detail.cartItemCount === 'number' && window.theme) {
      window.theme.cartItemCount = event.detail.cartItemCount;
    }
    scheduleHydration();
  });
  document.addEventListener('theme:cart:update', () => scheduleHydration());
  document.addEventListener('theme:line-item:change', (event) => scheduleHydration(event.detail?.cart));
  document.addEventListener('shopify:section:load', () => scheduleHydration());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleHydration(), { once: true });
  } else {
    scheduleHydration();
  }
})();
